/**
 * Teams SDK bootstrap + helpers.
 *
 * All interaction with `@microsoft/teams-js` is centralised here per the
 * wiring contract (teams-app-wiring §5.1).
 */

import * as teamsJs from '@microsoft/teams-js';

let cachedInitPromise: Promise<boolean> | null = null;
let cachedContext: teamsJs.app.Context | null = null;

/**
 * Canonical Teams-host detection: attempt `teamsJs.app.initialize()` with a
 * timeout. The initialize handshake succeeds only when a Teams host is on the
 * other side of postMessage, so this is the authoritative check.
 *
 * Why we don't use UA sniffing or `ancestorOrigins`:
 *   - Teams desktop web-view uses a regular Edge/Chrome UA (no 'teams/' token).
 *   - Microsoft migrated the Teams web host to `teams.cloud.microsoft` — older
 *     checks for `teams.microsoft.com` silently return false there.
 *   - `window.parent.location` access throws in a cross-origin iframe, which
 *     was masking the real detection intent with a SecurityError.
 *
 * 2500ms timeout is a balance: Teams host typically responds in <500ms, but
 * cold starts + slow corporate networks can push it over 1s. Browser-direct
 * resolves to `false` only after the timeout — acceptable (the "Open in Teams"
 * page then renders).
 */
export async function isInTeamsHost(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  try {
    await Promise.race([
      teamsJs.app.initialize(),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('teams-timeout')), 2500)),
    ]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Idempotent initialize — safe to call from many places.
 * Resolves `true` if running inside Teams, `false` if we're in a browser dev session.
 */
export async function initTeams(): Promise<boolean> {
  if (cachedInitPromise) return cachedInitPromise;

  cachedInitPromise = (async () => {
    const inTeams = await isInTeamsHost();
    if (!inTeams) return false;

    try {
      cachedContext = await teamsJs.app.getContext();
      // CRITICAL: tell Teams the app is ready. Without this call, when the
      // manifest has `showLoadingIndicator: true` (or in some host versions
      // by default), Teams waits for notifySuccess() and will tear the
      // iframe down on a ~45-60s timeout loop if it never arrives. Even with
      // showLoadingIndicator unset, calling this is the documented contract
      // for well-behaved Teams tab apps. Ignore errors: older Teams hosts
      // may not expose app.notifySuccess().
      try {
        teamsJs.app.notifySuccess();
      } catch { /* older host - fine */ }
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[teams] getContext failed after initialize:', err);
      try {
        teamsJs.app.notifyFailure({
          reason: teamsJs.app.FailedReason.Other,
          message: err instanceof Error ? err.message : String(err),
        });
      } catch { /* older host */ }
      return false;
    }
  })();

  return cachedInitPromise;
}

/** Returns the cached Teams context, or null if not initialised. */
export function getTeamsContext(): teamsJs.app.Context | null {
  return cachedContext;
}

/**
 * Acquire an SSO access token from the Teams host. Used by ApiClient to
 * attach `Authorization: Bearer <token>` on v3 + portal calls.
 *
 * @param resources Optional scopes / resources requested by the Function App.
 */
export async function getTeamsAuthToken(resources?: string[]): Promise<string> {
  await initTeams();
  return teamsJs.authentication.getAuthToken(
    resources !== undefined ? { resources } : undefined,
  );
}

/**
 * Show Teams' native profile card (photo, title, presence, chat/email actions) for a staff
 * member, anchored to an element. TeamsJS marks profile.showProfile beta, so the host decides:
 * returns false when this Teams client does not support it (the caller keeps its own tooltip).
 * Bill 2026-10-13: "take advantage of the Teams native user hover".
 */
export async function showTeamsProfile(
  upn: string,
  target: DOMRect,
  trigger: teamsJs.profile.TriggerType,
): Promise<boolean> {
  try {
    await initTeams();
    if (!teamsJs.profile.isSupported()) return false;
    await teamsJs.profile.showProfile({
      persona: { identifiers: { Upn: upn } },
      targetElementBoundingRect: target,
      triggerType: trigger,
      modality: 'Card',
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Teams deep link into this app's home tab carrying a sub-page id (e.g. "cart:{id}"), the same
 * link the API puts in cards (CardTemplateHelpers.TeamsAppDeepLink). useTeamsDeepLink routes on
 * it when the app opens. Manifest id d8c7f507-... (appPackage/manifest.json).
 */
export function teamsAppDeepLink(subPageId?: string | null): string {
  const url = 'https://teams.microsoft.com/l/entity/d8c7f507-3f20-40b3-bde1-a8af589a1ef1/home';
  return subPageId ? `${url}?context=${encodeURIComponent(JSON.stringify({ subEntityId: subPageId }))}` : url;
}

/** What a message from an officer card is about: shown as "Re: <title>" with a link. */
export interface StaffMessageReference {
  title: string;
  cartId?: string | null;
}

/** The "Re: ..." line for a chat draft (plain text - drafts cannot carry HTML links). */
export function referenceLine(ref: StaffMessageReference | null | undefined): string {
  if (!ref?.title) return '';
  return ref.cartId ? `Re: ${ref.title} - ${teamsAppDeepLink(`cart:${ref.cartId}`)}` : `Re: ${ref.title}`;
}

/** Open a 1:1 Teams chat with optional pre-filled message. */
export async function openChatWithStaff(upn: string, message?: string): Promise<void> {
  await initTeams();
  const params: teamsJs.chat.OpenSingleChatRequest = { user: upn };
  if (message !== undefined) params.message = message;
  await teamsJs.chat.openChat(params);
}

/** Start a Teams audio call. */
export async function startCallWithStaff(upn: string): Promise<void> {
  await initTeams();
  await teamsJs.call.startCall({
    targets: [upn],
    requestedModalities: [teamsJs.call.CallModalities.Audio],
  });
}

/** Start a Teams video call (officer profile card, Bill 2026-10-13). */
export async function startVideoCallWithStaff(upn: string): Promise<void> {
  await initTeams();
  await teamsJs.call.startCall({
    targets: [upn],
    requestedModalities: [teamsJs.call.CallModalities.Audio, teamsJs.call.CallModalities.Video],
  });
}

/** Compose a Teams meeting / Outlook appointment. */
export async function composeMeeting(params: {
  attendees: string[];
  subject?: string;
  startTime?: string;
  endTime?: string;
  content?: string;
}): Promise<void> {
  await initTeams();
  if (!teamsJs.calendar.isSupported()) return;
  const req: teamsJs.calendar.ComposeMeetingParams = {
    attendees: params.attendees,
  };
  if (params.subject !== undefined) req.subject = params.subject;
  if (params.startTime !== undefined) req.startTime = params.startTime;
  if (params.endTime !== undefined) req.endTime = params.endTime;
  if (params.content !== undefined) req.content = params.content;
  await teamsJs.calendar.composeMeeting(req);
}
